import { Module, GetterTree, MutationTree, ActionTree } from "vuex";
import { RootState } from "@/store/types";
import moment from "moment";
import {
  CreateScheduleRequest,
  EmployeeSchedules,
  ListEmployeeWithHourSchedule,
  PPDRow,
  Schedule,
  ScheduleCallOff,
  ScheduleChartData,
  ScheduleDailyReport,
  ScheduleService,
  ShiftBid,
  SchedulePpdService,
  AllSchedulePPD,
  DepartmentSchedulePPD,
  SchedulePPD,
  ScheduleSetting,
  ScheduleTimeOff,
  ScheduleTimeOffRequest,
  ScheduleTimeOffUpdateRequest,
  ScheduleTimeOffRejectRequest,
  ScheduleTimeOffService,
  SettingsPayload,
} from "@/api";
import { checkSelfRole } from "@/common/role_utils";
import { hourMinAdder } from "@/common/date_format";

const defaultViewStart = moment()
  .startOf("month")
  .format("YYYY-MM-DD");
const defaultViewEnd = moment()
  .endOf("month")
  .format("YYYY-MM-DD");

type ScheduleState = {
  schedules: Schedule[] | null;
  openShifts: Schedule[] | null;
  shiftBids: ShiftBid[] | null;
  schedulePPD: PPDRow[] | null;
  schedulePPDFallback: PPDRow[] | null;
  employees: ListEmployeeWithHourSchedule[] | null;
  chartData: ScheduleChartData | null;
  dailyReport: ScheduleDailyReport | null;
  schedulePpdDetail: (AllSchedulePPD | DepartmentSchedulePPD)[] | null;
  currentStart: string;
  currentEnd: string;
  lastSocketUpdate: number;
  timeOffRequests: ScheduleTimeOff[] | null;
  scheduleSettings: ScheduleSetting | null;
};

const state: ScheduleState = {
  schedules: null,
  openShifts: null,
  shiftBids: null,
  schedulePPD: null,
  schedulePPDFallback: null,
  employees: null,
  chartData: null,
  dailyReport: null,
  schedulePpdDetail: null,
  currentStart: defaultViewStart,
  currentEnd: defaultViewEnd,
  lastSocketUpdate: 0,
  timeOffRequests: null,
  scheduleSettings: null,
};

const getters: GetterTree<ScheduleState, RootState> = {
  schedules: state => state.schedules,
  openShifts: state => state.openShifts,
  shiftBids: state => state.shiftBids,
  schedulePPD: state => state.schedulePPD,
  schedulePPDFallback: state => state.schedulePPDFallback,
  employees: state => state.employees,
  chartData: state => state.chartData,
  dailyReport: state => state.dailyReport,
  schedulePpdDetail: state => state.schedulePpdDetail,
  viewRange: state => ({ start: state.currentStart, end: state.currentEnd }),
  timeOffRequests: state => state.timeOffRequests,
  scheduleSettings: state => state.scheduleSettings,
};

const mutations: MutationTree<ScheduleState> = {
  SET_SCHEDULES(state, schedules: Schedule[]) {
    state.schedules = schedules;
  },
  SET_OPEN_SHIFTS(state, schedules: Schedule[]) {
    state.openShifts = schedules;
  },
  SET_VIEW_RANGE(state, { start, end }: { start: string; end: string }) {
    state.currentStart = start;
    state.currentEnd = end;
  },
  SOCKET_REFRESH(state) {
    state.lastSocketUpdate = Date.now();
  },
  ADD_SCHEDULE(state, schedule: Schedule) {
    if (state.schedules == null) {
      state.schedules = [];
    }
    state.schedules.push(schedule);
  },
  UPDATE_SCHEDULE(state, updatedSchedule: Schedule) {
    if (state.schedules) {
      const index = state.schedules.findIndex(
        schedule => schedule.id === updatedSchedule.id,
      );
      console.log("Index Found: ", index);
      if (index !== -1) {
        state.schedules.splice(index, 1, updatedSchedule);
      }
    }
  },
  REMOVE_SCHEDULE(state, scheduleId: number) {
    if (state.schedules) {
      const index = state.schedules.findIndex(
        schedule => schedule.id === scheduleId,
      );
      if (index !== -1) {
        state.schedules.splice(index, 1);
      }
    }
  },
  SET_SHIFT_BIDS(state, shiftBids: ShiftBid[]) {
    state.shiftBids = shiftBids;
  },
  ADD_SHIFT_BID(state, shiftBid: ShiftBid) {
    if (state.shiftBids == null) {
      state.shiftBids = [];
    }
    state.shiftBids.push(shiftBid);
  },
  UPDATE_SHIFT_BID(state, updatedShiftBid: ShiftBid) {
    if (state.shiftBids) {
      const index = state.shiftBids.findIndex(
        shiftBid => shiftBid.id === updatedShiftBid.id,
      );
      if (index !== -1) {
        state.shiftBids.splice(index, 1, updatedShiftBid);
      }
    }
  },
  REMOVE_SHIFT_BID(state, shiftBidId: number) {
    if (state.shiftBids) {
      const index = state.shiftBids.findIndex(
        shiftBid => shiftBid.id === shiftBidId,
      );
      if (index !== -1) {
        state.shiftBids.splice(index, 1);
      }
    }
  },
  SET_SCHEDULE_PPD(state, schedulePPD: PPDRow[]) {
    state.schedulePPD = schedulePPD;
  },
  SET_SCHEDULE_PPD_FALLBACK(state, schedulePPD: PPDRow[]) {
    state.schedulePPDFallback = schedulePPD;
  },
  SET_EMPLOYEES(state, employees: ListEmployeeWithHourSchedule[]) {
    state.employees = employees;
  },
  SET_CHART_DATA(state, chartData: ScheduleChartData) {
    state.chartData = chartData;
  },
  SET_DAILY_REPORT(state, report: ScheduleDailyReport) {
    state.dailyReport = report;
  },
  SET_SCHEDULE_PPD_DETAIL(
    state,
    detail: (AllSchedulePPD | DepartmentSchedulePPD)[],
  ) {
    state.schedulePpdDetail = detail;
  },
  SET_TIME_OFF_REQUESTS(state, requests: ScheduleTimeOff[]) {
    state.timeOffRequests = requests;
  },
  ADD_TIME_OFF_REQUEST(state, request: ScheduleTimeOff) {
    if (state.timeOffRequests == null) {
      state.timeOffRequests = [];
    }
    state.timeOffRequests.push(request);
  },
  UPDATE_TIME_OFF_REQUEST(state, updated: ScheduleTimeOff) {
    if (state.timeOffRequests) {
      const index = state.timeOffRequests.findIndex(r => r.id === updated.id);
      if (index !== -1) {
        state.timeOffRequests.splice(index, 1, updated);
      }
    }
  },
  REMOVE_TIME_OFF_REQUEST(state, id: number) {
    if (state.timeOffRequests) {
      const index = state.timeOffRequests.findIndex(r => r.id === id);
      if (index !== -1) {
        state.timeOffRequests.splice(index, 1);
      }
    }
  },
  SET_SCHEDULE_SETTINGS(state, settings: ScheduleSetting) {
    state.scheduleSettings = settings;
  },
};

const actions: ActionTree<ScheduleState, RootState> = {
  async fetchSchedules(
    { commit, rootState },
    payload?: { start?: string; end?: string; department?: number },
  ) {
    const companyId = rootState.appContext.company?.id;
    if (!companyId) {
      throw new Error("Company ID is not available in the state.");
    }

    try {
      const schedules = await ScheduleService.getScheduleList({
        companyId,
        start: payload?.start,
        end: payload?.end,
        department: payload?.department,
      });
      commit("SET_SCHEDULES", schedules);
    } catch (error) {
      if (error instanceof Error) {
        commit("alert/SET_SHOW_ERROR", error.message, { root: true });
      }
    }
  },
  /**
   * Fetches ALL open shifts (status=OPEN) across all dates — not restricted
   * to the current calendar view range. Used exclusively by the Open Shifts tab.
   */
  async fetchOpenShifts({ commit, rootState }) {
    const companyId = rootState.appContext.company?.id;
    if (!companyId) {
      throw new Error("Company ID is not available in the state.");
    }

    try {
      const schedules = await ScheduleService.getScheduleList({
        companyId,
        status: "OPEN",
      });
      commit("SET_OPEN_SHIFTS", schedules);
    } catch (error) {
      if (error instanceof Error) {
        commit("alert/SET_SHOW_ERROR", error.message, { root: true });
      }
    }
  },
  async fetchChartData(
    { commit, rootState },
    payload?: { start?: string; end?: string },
  ) {
    const companyId = rootState.appContext.company?.id;
    if (!companyId) {
      throw new Error("Company ID is not available in the state.");
    }

    try {
      const start = payload?.start
        ? moment(payload.start)
            .startOf("day")
            .toISOString()
        : undefined;
      const end = payload?.end
        ? moment(payload.end)
            .endOf("day")
            .toISOString()
        : undefined;

      const chartData = await ScheduleService.getScheduleChartData({
        companyId,
        start,
        end,
      });
      commit("SET_CHART_DATA", chartData);
    } catch (error) {
      if (error instanceof Error) {
        commit("alert/SET_SHOW_ERROR", error.message, { root: true });
      }
    }
  },
  async fetchDailyReport(
    { commit, rootState },
    payload: { date: string; department?: number },
  ) {
    const companyId = rootState.appContext.company?.id;
    if (!companyId) {
      throw new Error("Company ID is not available in the state.");
    }

    try {
      const dailyReport = await ScheduleService.getScheduleService({
        companyId,
        date: payload.date,
        department: payload.department,
      });
      commit("SET_DAILY_REPORT", dailyReport);
    } catch (error) {
      if (error instanceof Error) {
        commit("alert/SET_SHOW_ERROR", error.message, { root: true });
      }
    }
  },
  async addSchedule(
    { commit, rootState, dispatch },
    {
      schedule,
      start,
      end,
      department,
      titleId,
    }: {
      schedule: CreateScheduleRequest;
      start?: string;
      end?: string;
      department?: number;
      titleId?: number;
    },
  ) {
    const companyId = rootState.appContext.company?.id;
    if (!companyId) {
      throw new Error("Company ID is not available in the state.");
    }

    try {
      schedule.companyId = companyId;
      const newSchedules = await ScheduleService.createSchedule({
        companyId,
        requestBody: schedule,
      });
      for (const s of newSchedules) {
        commit("ADD_SCHEDULE", s);
      }
      dispatch("getSchedulePPD", { start, end, department, titleId });
    } catch (error) {
      if (error instanceof Error) {
        commit("alert/SET_SHOW_ERROR", error.message, { root: true });
      }
    }
  },
  async updateSchedule(
    { commit, rootState, dispatch },
    {
      schedule,
      start,
      end,
      department,
      titleId,
    }: {
      schedule: Schedule;
      start?: string;
      end?: string;
      department?: number;
      titleId?: number;
    },
  ) {
    const companyId = rootState.appContext.company?.id;
    if (!companyId) {
      throw new Error("Company ID is not available in the state.");
    }

    try {
      const updatedSchedule = await ScheduleService.updateSchedule({
        companyId,
        scheduleId: schedule.id,
        requestBody: schedule,
      });
      commit("UPDATE_SCHEDULE", updatedSchedule);
      dispatch("getSchedulePPD", { start, end, department, titleId });
    } catch (error) {
      if (error instanceof Error) {
        commit("alert/SET_SHOW_ERROR", error.message, { root: true });
      }
    }
  },
  async updateScheduleStatus({ commit, rootState }, schedule: Schedule) {
    const companyId = rootState.appContext.company?.id;
    if (!companyId) {
      throw new Error("Company ID is not available in the state.");
    }

    try {
      const updatedSchedule = await ScheduleService.updateScheduleStatus({
        companyId,
        scheduleId: schedule.id,
        requestBody: schedule,
      });
      commit("UPDATE_SCHEDULE", updatedSchedule);
    } catch (error) {
      if (error instanceof Error) {
        commit("alert/SET_SHOW_ERROR", error.message, { root: true });
      }
    }
  },
  async removeSchedule(
    { commit, rootState, dispatch },
    {
      scheduleId,
      start,
      end,
    }: { scheduleId: number; start?: string; end?: string },
  ) {
    const companyId = rootState.appContext.company?.id;
    if (!companyId) {
      throw new Error("Company ID is not available in the state.");
    }

    try {
      await ScheduleService.deleteSchedule({
        companyId,
        scheduleId,
      });
      commit("REMOVE_SCHEDULE", scheduleId);
      dispatch("getSchedulePPD", { start, end, undefined });
    } catch (error) {
      if (error instanceof Error) {
        commit("alert/SET_SHOW_ERROR", error.message, { root: true });
      }
    }
  },
  async fetchShiftBids({ commit, rootState }, scheduleId: number) {
    const companyId = rootState.appContext.company?.id;
    if (!companyId) {
      throw new Error("Company ID is not available in the state.");
    }

    try {
      const shiftBids = await ScheduleService.getShiftBid({
        companyId,
        scheduleId,
      });
      commit("SET_SHIFT_BIDS", shiftBids);
    } catch (error) {
      if (error instanceof Error) {
        commit("alert/SET_SHOW_ERROR", error.message, { root: true });
      }
    }
  },
  async addShiftBid(
    { commit, rootState },
    { scheduleId, shiftBid }: { scheduleId: number; shiftBid: ShiftBid },
  ) {
    const companyId = rootState.appContext.company?.id;
    const bidder = rootState.appContext.self?.id;
    if (!companyId || !bidder) {
      throw new Error("Company ID or Bidder is not available in the state.");
    }

    try {
      shiftBid.bidder = bidder;
      const newShiftBid = await ScheduleService.createShiftBid({
        companyId,
        scheduleId,
        requestBody: shiftBid,
      });
      commit("ADD_SHIFT_BID", newShiftBid);
    } catch (error) {
      if (error instanceof Error) {
        commit("alert/SET_SHOW_ERROR", error.message, { root: true });
      }
    }
  },
  async updateShiftBidStatus({ commit, rootState }, { scheduleId, shiftBid }) {
    const companyId = rootState.appContext.company?.id;
    if (!companyId) {
      throw new Error("Company ID is not available in the state.");
    }

    try {
      const updatedShiftBid = await ScheduleService.updateShiftBidStatus({
        companyId,
        scheduleId,
        requestBody: shiftBid,
      });
      commit("UPDATE_SHIFT_BID", updatedShiftBid);
    } catch (error) {
      if (error instanceof Error) {
        commit("alert/SET_SHOW_ERROR", error.message, { root: true });
      }
    }
  },
  async getSchedulePPD(
    { commit, rootState },
    payload?: {
      start?: string;
      end?: string;
      department?: number;
      titleId?: number;
    },
  ) {
    const companyId = rootState.appContext.company?.id;
    if (!companyId) {
      throw new Error("Company ID is not available in the state.");
    }

    try {
      const schedulePPD = await ScheduleService.getSchedulePpdList({
        companyId,
        start: payload?.start,
        end: payload?.end,
        department: payload?.department,
        titleId: payload?.titleId,
      });
      commit("SET_SCHEDULE_PPD", schedulePPD);
    } catch (error) {
      if (error instanceof Error) {
        commit("alert/SET_SHOW_ERROR", error.message, { root: true });
      }
    }
  },
  /**
   * Fetches department-level PPD (no title filter) and stores it in the
   * fallback slot. Used as a fallback when a title-filtered PPD returns no
   * data for a given day.
   */
  async getSchedulePPDFallback(
    { commit, rootState },
    payload?: { start?: string; end?: string; department?: number },
  ) {
    const companyId = rootState.appContext.company?.id;
    if (!companyId) {
      throw new Error("Company ID is not available in the state.");
    }

    try {
      const schedulePPD = await ScheduleService.getSchedulePpdList({
        companyId,
        start: payload?.start,
        end: payload?.end,
        department: payload?.department,
      });
      commit("SET_SCHEDULE_PPD_FALLBACK", schedulePPD);
    } catch (error) {
      if (error instanceof Error) {
        commit("alert/SET_SHOW_ERROR", error.message, { root: true });
      }
    }
  },

  async getSchedulePpdDetail(
    { commit, rootState },
    payload?: { date?: string },
  ) {
    const companyId = rootState.appContext.company?.id;
    if (!companyId) {
      throw new Error("Company ID is not available in the state.");
    }

    try {
      if (checkSelfRole("owner")) {
        const detail = await SchedulePpdService.getAllPpd({
          companyId,
          date: payload?.date,
        });
        commit("SET_SCHEDULE_PPD_DETAIL", detail);
      } else {
        const detail = await SchedulePpdService.getDepartmentPpd({
          companyId,
          date: payload?.date,
        });
        commit("SET_SCHEDULE_PPD_DETAIL", detail);
      }
    } catch (error) {
      if (error instanceof Error) {
        commit("alert/SET_SHOW_ERROR", error.message, { root: true });
      }
    }
  },
  async saveSchedulePpd(
    { commit, rootState, dispatch },
    records: SchedulePPD[],
  ) {
    const companyId = rootState.appContext.company?.id;
    if (!companyId) {
      throw new Error("Company ID is not available in the state.");
    }

    try {
      // POST /schedule-ppd is backed by UpsertPPDBulk: updates-or-inserts
      // each record keyed on (department_id, department_title_id).
      const result = await SchedulePpdService.addSchedulePpd({
        companyId,
        requestBody: records,
      });
      const date = records[0]?.date;
      if (date) {
        await dispatch("getSchedulePpdDetail", { date });
      }
      return result;
    } catch (error) {
      if (error instanceof Error) {
        commit("alert/SET_SHOW_ERROR", error.message, { root: true });
      }
      throw error;
    }
  },
  async sendScheduleSMS({ commit, rootState }, payload: EmployeeSchedules[]) {
    const companyId = rootState.appContext.company?.id;
    if (!companyId) {
      throw new Error("Company ID is not available in the state.");
    }

    try {
      const response = await ScheduleService.sendScheduleSms({
        companyId,
        requestBody: payload,
      });
      return response;
    } catch (error) {
      if (error instanceof Error) {
        commit("alert/SET_SHOW_ERROR", error.message, { root: true });
      }
      throw error;
    }
  },
  async listEmployeList({ commit, rootState }) {
    const companyId = rootState.appContext.company?.id;
    if (!companyId) {
      throw new Error("Company ID is not available in the state.");
    }

    try {
      const response = await ScheduleService.listEmployeesWithScheduleHours({
        companyId,
      });
      commit("SET_EMPLOYEES", response);
    } catch (error) {
      if (error instanceof Error) {
        commit("alert/SET_SHOW_ERROR", error.message, { root: true });
      }
      throw error;
    }
  },
  async callOffSchedule(
    { commit, rootState, dispatch, getters },
    { schedule, note }: { schedule: Schedule; note?: string },
  ) {
    const companyId = rootState.appContext.company?.id;
    if (!companyId) {
      throw new Error("Company ID is not available in the state.");
    }

    try {
      const callOff: ScheduleCallOff = {
        ScheduleId: schedule.id,
        companyId,
        departmentId: schedule.departmentId,
        facilityId: schedule.facilityId ?? 0,
        eventMetricId: 0,
        assignee: schedule.assignee ?? 0,
        postedBy: rootState.appContext.self?.id ?? null,
        postedByEmployeeDepartmentId:
          rootState.appContext.self?.primaryDepartmentId ?? null,
        note: note ?? null,
        eventTime: hourMinAdder(schedule.date),
        date: schedule.date,
        startTime: schedule.startTime,
        endTime: schedule.endTime,
      };

      await ScheduleService.updateScheduleCallOff({
        companyId,
        scheduleId: schedule.id,
        requestBody: callOff,
      });

      dispatch("fetchSchedules", {
        start: getters["currentStart"],
        end: getters["currentEnd"],
      });
    } catch (error) {
      if (error instanceof Error) {
        commit("alert/SET_SHOW_ERROR", error.message, { root: true });
      }
    }
  },

  // ─── Schedule Time Off ────────────────────────────────────────────────────

  async fetchTimeOffRequests(
    { commit, rootState },
    payload: { startDate: string; endDate: string },
  ) {
    const companyId = rootState.appContext.company?.id;
    if (!companyId) {
      throw new Error("Company ID is not available in the state.");
    }

    try {
      const requests = await ScheduleTimeOffService.getScheduleTimeOffByCompany(
        {
          companyId,
          startDate: payload.startDate,
          endDate: payload.endDate,
        },
      );
      commit("SET_TIME_OFF_REQUESTS", requests);
    } catch (error) {
      if (error instanceof Error) {
        commit("alert/SET_SHOW_ERROR", error.message, { root: true });
      }
    }
  },

  async createTimeOff(
    { commit, rootState },
    requestBody: ScheduleTimeOffRequest,
  ) {
    const companyId = rootState.appContext.company?.id;
    if (!companyId) {
      throw new Error("Company ID is not available in the state.");
    }

    try {
      const created = await ScheduleTimeOffService.createScheduleTimeOff({
        companyId,
        requestBody,
      });
      for (const entry of created) {
        commit("ADD_TIME_OFF_REQUEST", entry);
      }
      return created;
    } catch (error) {
      if (error instanceof Error) {
        commit("alert/SET_SHOW_ERROR", error.message, { root: true });
      }
      throw error;
    }
  },

  async updateTimeOff(
    { commit, rootState },
    {
      id,
      requestBody,
    }: { id: number; requestBody: ScheduleTimeOffUpdateRequest },
  ) {
    const companyId = rootState.appContext.company?.id;
    if (!companyId) {
      throw new Error("Company ID is not available in the state.");
    }

    try {
      const updated = await ScheduleTimeOffService.updateScheduleTimeOff({
        companyId,
        scheduleTimeOffId: id,
        requestBody,
      });
      commit("UPDATE_TIME_OFF_REQUEST", updated);
      return updated;
    } catch (error) {
      if (error instanceof Error) {
        commit("alert/SET_SHOW_ERROR", error.message, { root: true });
      }
      throw error;
    }
  },

  async deleteTimeOff({ commit, rootState }, id: number) {
    const companyId = rootState.appContext.company?.id;
    if (!companyId) {
      throw new Error("Company ID is not available in the state.");
    }

    try {
      await ScheduleTimeOffService.deleteScheduleTimeOff({
        companyId,
        scheduleTimeOffId: id,
      });
      commit("REMOVE_TIME_OFF_REQUEST", id);
    } catch (error) {
      if (error instanceof Error) {
        commit("alert/SET_SHOW_ERROR", error.message, { root: true });
      }
      throw error;
    }
  },

  async acceptTimeOff({ commit, rootState }, id: number) {
    const companyId = rootState.appContext.company?.id;
    if (!companyId) {
      throw new Error("Company ID is not available in the state.");
    }

    try {
      const updated = await ScheduleTimeOffService.acceptScheduleTimeOff({
        companyId,
        scheduleTimeOffId: id,
      });
      commit("UPDATE_TIME_OFF_REQUEST", updated);
      return updated;
    } catch (error) {
      if (error instanceof Error) {
        commit("alert/SET_SHOW_ERROR", error.message, { root: true });
      }
      throw error;
    }
  },

  async rejectTimeOff(
    { commit, rootState },
    {
      id,
      requestBody,
    }: { id: number; requestBody: ScheduleTimeOffRejectRequest },
  ) {
    const companyId = rootState.appContext.company?.id;
    if (!companyId) {
      throw new Error("Company ID is not available in the state.");
    }

    try {
      const updated = await ScheduleTimeOffService.rejectScheduleTimeOff({
        companyId,
        scheduleTimeOffId: id,
        requestBody,
      });
      commit("UPDATE_TIME_OFF_REQUEST", updated);
      return updated;
    } catch (error) {
      if (error instanceof Error) {
        commit("alert/SET_SHOW_ERROR", error.message, { root: true });
      }
      throw error;
    }
  },

  async cancelTimeOff({ commit, rootState }, id: number) {
    const companyId = rootState.appContext.company?.id;
    if (!companyId) {
      throw new Error("Company ID is not available in the state.");
    }

    try {
      const updated = await ScheduleTimeOffService.cancelScheduleTimeOff({
        companyId,
        scheduleTimeOffId: id,
      });
      commit("UPDATE_TIME_OFF_REQUEST", updated);
      return updated;
    } catch (error) {
      if (error instanceof Error) {
        commit("alert/SET_SHOW_ERROR", error.message, { root: true });
      }
      throw error;
    }
  },

  async fetchScheduleSettings({ commit, rootState }) {
    const companyId = rootState.appContext.company?.id;
    if (!companyId) {
      throw new Error("Company ID is not available in the state.");
    }

    try {
      const settings = await ScheduleService.getScheduleSettings({ companyId });
      commit("SET_SCHEDULE_SETTINGS", settings);
      return settings;
    } catch (error) {
      if (error instanceof Error) {
        commit("alert/SET_SHOW_ERROR", error.message, { root: true });
      }
      throw error;
    }
  },

  async upsertScheduleSettings(
    { commit, rootState },
    requestBody: SettingsPayload,
  ) {
    const companyId = rootState.appContext.company?.id;
    if (!companyId) {
      throw new Error("Company ID is not available in the state.");
    }

    try {
      const settings = await ScheduleService.upsertScheduleSettings({
        companyId,
        requestBody,
      });
      commit("SET_SCHEDULE_SETTINGS", settings);
      return settings;
    } catch (error) {
      if (error instanceof Error) {
        commit("alert/SET_SHOW_ERROR", error.message, { root: true });
      }
      throw error;
    }
  },
};

export const schedule: Module<ScheduleState, RootState> = {
  namespaced: true,
  state,
  getters,
  mutations,
  actions,
};
